import type { ClientMessage, ServerMessage } from '../shared/types.js';
import { ClockSync } from './clockSync.js';
import { num, t } from './i18n/i18n.js';
import { SampleRing } from './sampleRing.js';

/**
 * WebSocket client with automatic reconnection.
 *
 * The player id is remembered in sessionStorage and replayed on every `hello`, which is
 * what makes a dropped connection mid-race a recoverable hiccup: the server matches the
 * id, hands back the same slot with its checkpoint progress intact, and the race carries
 * on for everyone else regardless.
 */

export type NetHandler = (msg: ServerMessage) => void;

export type NetStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

const STORAGE_KEY = 'osm-racer.session';

interface StoredSession {
  code: string;
  playerId: string;
}

export class Net {
  private socket: WebSocket | null = null;
  private handlers: NetHandler[] = [];
  private statusHandlers: ((status: NetStatus, detail?: string) => void)[] = [];
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private closedByUs = false;

  code = '';
  name = 'Racer';
  color = 0xe8503a;
  playerId: string | null = null;
  status: NetStatus = 'closed';
  /** Round-trip time in ms, refreshed every few seconds. */
  latency = 0;
  /** The last minute of those, one every four seconds, for the perf graph. */
  readonly latencies = new SampleRing(16);
  /** The server's clock, off the same pings. What the countdown is converted with. */
  readonly clock = new ClockSync();

  private pingTimer: number | null = null;
  private pingSentAt = 0;
  private pingSeq = 0;

  on(handler: NetHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler);
    };
  }

  onStatus(handler: (status: NetStatus, detail?: string) => void): () => void {
    this.statusHandlers.push(handler);
    return () => {
      this.statusHandlers = this.statusHandlers.filter((h) => h !== handler);
    };
  }

  private setStatus(status: NetStatus, detail?: string): void {
    this.status = status;
    for (const handler of this.statusHandlers) handler(status, detail);
  }

  /** `code` may be a 4-letter lobby code, or "NEW" to have the server allocate one. */
  connect(code: string, name: string, color: number): void {
    this.code = code.toUpperCase();
    this.name = name;
    this.color = color;
    this.closedByUs = false;

    const stored = readSession();
    if (stored && stored.code === this.code) this.playerId = stored.playerId;

    this.open();
  }

  private open(): void {
    this.setStatus(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      this.setStatus('open');
      this.send({
        t: 'hello',
        code: this.code,
        name: this.name,
        color: this.color,
        ...(this.playerId ? { playerId: this.playerId } : {}),
      });
      this.startPing();
    });

    socket.addEventListener('message', (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }
      if (msg.t === 'welcome') {
        this.playerId = msg.playerId;
        this.code = msg.lobby.code;
        writeSession({ code: this.code, playerId: msg.playerId });
      }
      if (msg.t === 'pong') {
        if (msg.n === this.pingSeq) {
          const receivedAt = performance.now();
          this.latency = Math.round(receivedAt - this.pingSentAt);
          this.latencies.push(this.latency);
          this.clock.add(this.pingSentAt, receivedAt, msg.now);
        }
        return;
      }
      for (const handler of this.handlers) handler(msg);
    });

    socket.addEventListener('close', () => {
      this.stopPing();
      if (this.closedByUs) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // `close` always follows; nothing useful to do here beyond letting it fire.
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.reconnectAttempt++;
    // Capped exponential backoff: 0.4s, 0.8s, 1.6s, ... up to 6s.
    const delay = Math.min(6000, 400 * 2 ** (this.reconnectAttempt - 1));
    this.setStatus('reconnecting', t('retrying in {seconds}s', { seconds: num(delay / 1000, 1) }));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    const beat = () => {
      this.pingSeq++;
      this.pingSentAt = performance.now();
      this.send({ t: 'ping', n: this.pingSeq });
    };
    beat();
    this.pingTimer = window.setInterval(beat, 4000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  send(msg: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(msg));
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopPing();
    this.socket?.close();
    this.socket = null;
    this.setStatus('closed');
  }

  forgetSession(): void {
    this.playerId = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private browsing */
    }
  }
}

function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* private browsing — reconnect simply won't restore progress */
  }
}
